import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import SEO from './SEO';
import {
  SunIcon,
  MoonIcon,
  DocIcon,
  MailIcon,
  PinIcon,
  CogIcon,
  CodeIcon,
  SearchIcon,
  MobileIcon,
  CloudIcon,
  DatabaseIcon,
  SparklesIcon,
  TrophyIcon,
  ExternalLinkIcon,
  CopyIcon,
  CheckIcon,
  UsersIcon,
  ActivityIcon,
  LayersIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  ShieldIcon,
  LockIcon,
  BoltIcon,
} from './components/Icons';
import './BragPage.css';

export default function BragPage() {
  const [theme, setTheme] = useState('bright'); // 'bright' | 'obsidian'
  const [activeCategory, setActiveCategory] = useState('all');
  const [copiedRfp, setCopiedRfp] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);

  // Ensure window is at top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Synchronize theme to root
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  const handleToggleTheme = () => {
    setTheme((prev) => (prev === 'bright' ? 'obsidian' : 'bright'));
  };

  const handleCopyRfp = () => {
    const text = `BRAMHAWARE TECHNOLOGIES // STUDIO SUMMARY & TRACK RECORD
Headquarters: Pune, Maharashtra, India
Website: https://bramhaware.in
Email: info@bramhaware.in

KEY MILESTONES & CAPABILITIES:
- The Foundora (thefoundora.com): National Startup & Investor Network indexing 2,400+ ventures across 28 Indian States.
- Dnyanaai Astrology (bramhaware.in/astrology): High-precision computational Vedic astrology algorithmic platform.
- InvoiceGen (bramhaware.in/invoicegen): 3D Smart GST Invoicing & Automated Tax Ledger engine.
- AttendMax & NewsApp: Biometric workforce telemetry and Spring Boot distributed microservices.
- Technical SEO & AEO: Sub-second Core Web Vitals (<1.2s LCP), deep Schema.org graphs, and edge pre-rendering.
- Atulya Yuva Foundation: Non-profit digital infrastructure supporting 10,000+ youth and emergency blood networks.
- Core Engineering: React, Next.js, Node.js, Spring Boot, PostgreSQL, Kafka, Flutter, AWS & Azure.`;

    navigator.clipboard.writeText(text).then(() => {
      setCopiedRfp(true);
      setTimeout(() => setCopiedRfp(false), 2500);
    });
  };

  const handleCopyShareLink = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2500);
    });
  };

  const handleDownloadBragPDF = () => {
    setIsPdfGenerating(true);
    try {
      const doc = new jsPDF();

      // Header
      doc.setFontSize(22);
      doc.setTextColor(15, 23, 42);
      doc.text('BRAMHAWARE TECHNOLOGIES', 20, 22);

      doc.setFontSize(10);
      doc.setTextColor(37, 99, 235);
      doc.text('OFFICIAL STUDIO BRAG SHEET & PRODUCTION TRACK RECORD', 20, 28);

      doc.setDrawColor(203, 213, 225);
      doc.line(20, 33, 190, 33);

      // Section 1: Key Numbers
      doc.setFontSize(12);
      doc.setTextColor(15, 23, 42);
      doc.text('1. Studio Numbers & Verified Scale', 20, 42);

      doc.setFontSize(9);
      doc.setTextColor(51, 65, 85);
      doc.text('• 2,400+ Startups Indexed across 28 Indian States via The Foundora network.', 25, 50);
      doc.text('• 10,000+ Citizens & Youth Empowered via Atulya Yuva Foundation & Emergency Bloodline.', 25, 57);
      doc.text('• 99.9% Cloud Uptime SLA across distributed AWS/Azure multi-tier topologies.', 25, 64);
      doc.text('• <1.2s Average LCP Core Web Vitals with proprietary edge pre-rendering & AEO architecture.', 25, 71);
      doc.text('• 100+ Production Deployments across enterprise SaaS, web apps, and mobile ecosystems.', 25, 78);

      // Section 2: Flagship Products
      doc.setFontSize(12);
      doc.setTextColor(15, 23, 42);
      doc.text('2. Shipped Software Products & In-House Platforms', 20, 92);

      doc.setFontSize(9);
      doc.setTextColor(30, 41, 59);
      doc.text('• The Foundora (thefoundora.com): India’s curated national startup discovery & investor syndicate engine.', 25, 100);
      doc.text('• Dnyanaai Astrology: Proprietary Vedic computational engine with automated astronomical calculations.', 25, 107);
      doc.text('• InvoiceGen: 3D Smart GST Invoicing & Automated Tax Ledger engine with client-side PDF rendering.', 25, 114);
      doc.text('• AttendMax: Biometric workforce telemetry and shift choreography for industrial enterprises.', 25, 121);
      doc.text('• NewsApp: Distributed digital newsroom microservices built with Spring Boot & Apache Kafka.', 25, 128);

      // Section 3: Technical Standards
      doc.setFontSize(12);
      doc.setTextColor(15, 23, 42);
      doc.text('3. Architecture Discipline & Compliance', 20, 142);

      doc.setFontSize(9);
      doc.setTextColor(51, 65, 85);
      doc.text('• 3-Tier Security Isolation: Global Edge CDN (Tier 1), Logic Mesh (Tier 2), Multi-Tenant Vault (Tier 3).', 25, 150);
      doc.text('• DPDP Act (India) & GDPR Data Protection Guardrails with Row-Level Security & AES-256 persistence.', 25, 157);
      doc.text('• Sub-Second Core Web Vitals optimization for instant search indexing and AI answer engines.', 25, 164);

      // Section 4: Contact & Verification
      doc.setFontSize(12);
      doc.setTextColor(15, 23, 42);
      doc.text('4. Studio Contact & RFP Verification', 20, 178);

      doc.setFontSize(9);
      doc.setTextColor(71, 85, 105);
      doc.text('Headquarters: Pune, Maharashtra, India | Email: info@bramhaware.in | Web: https://bramhaware.in', 25, 186);

      doc.save('Bramhaware_Technologies_Studio_Brag_Sheet.pdf');
    } catch (err) {
      console.error('Error generating PDF:', err);
    } finally {
      setIsPdfGenerating(false);
    }
  };

  // Structured Schema for Brag Page
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "BRAMHAWARE Studio Brag Sheet & Verified Track Record",
    "url": "https://bramhaware.in/brag",
    "description": "The official track record, shipped platforms, verified metrics, and technical accomplishments of Bramhaware Technologies.",
    "publisher": {
      "@type": "Organization",
      "name": "BRAMHAWARE Technologies",
      "url": "https://bramhaware.in",
      "logo": "https://bramhaware.in/bramhaware-logo-square.png"
    },
    "mainEntity": {
      "@type": "ItemList",
      "itemListElement": [
        {
          "@type": "SoftwareApplication",
          "position": 1,
          "name": "The Foundora",
          "url": "https://thefoundora.com",
          "applicationCategory": "BusinessApplication",
          "operatingSystem": "All",
          "description": "National Startup & Investor Discovery Network indexing 2,400+ ventures across 28 Indian States."
        },
        {
          "@type": "SoftwareApplication",
          "position": 2,
          "name": "Dnyanaai Astrology",
          "url": "https://bramhaware.in/astrology",
          "applicationCategory": "LifestyleApplication",
          "operatingSystem": "All",
          "description": "Proprietary computational Vedic astrology engine calculating real-time planetary transits and birth charts."
        },
        {
          "@type": "SoftwareApplication",
          "position": 3,
          "name": "InvoiceGen",
          "url": "https://bramhaware.in/invoicegen",
          "applicationCategory": "FinanceApplication",
          "operatingSystem": "All",
          "description": "3D Smart GST Invoicing and Automated Tax Ledger Engine."
        }
      ]
    }
  };

  // Brag Milestones List
  const bragItems = [
    {
      id: 'foundora',
      category: 'saas',
      title: 'The Foundora',
      subtitle: 'National Startup & Investor Discovery Engine',
      badge: 'FLAGSHIP PLATFORM',
      metric: '2,400+ Ventures • 28 States',
      desc: 'Conceived, architected, coded, and deployed 100% in-house by Bramhaware. Solves the discovery gap for Indian emerging startups with sub-100ms multi-facet indexing and direct investor syndicate conduits.',
      highlights: [
        'PostgreSQL partitioned index handling rapid founder searches across all Indian regions',
        'In-memory Redis caching delivering sub-100ms query response times',
        'Verified founder profiles with investor matching workflows',
      ],
      techStack: ['React', 'Node.js', 'PostgreSQL', 'Redis', 'Tailwind', 'CloudFront CDN'],
      link: 'https://thefoundora.com',
      linkText: 'Launch thefoundora.com',
      isExternal: true,
      icon: <LayersIcon />,
    },
    {
      id: 'dnyanaai',
      category: 'saas',
      title: 'Dnyanaai Astrology',
      subtitle: 'Proprietary Computational Vedic Astrology Engine',
      badge: 'PROPRIETARY ALGORITHMS',
      metric: 'Instant Ephemeris Calculation • Confidential Telemetry',
      desc: 'A modern mathematical synthesis of classical Vedic astronomy. Calculates precision planetary positions, divisional charts (D1, D9, D10), and career roadmaps without human error or bias.',
      highlights: [
        'Complex astronomical orbital celestial math converted to microsecond algorithms',
        'Interactive 3D WebGL celestial sphere with real-time responsive shaders',
        'End-to-end encrypted consultation booking and report distribution',
      ],
      techStack: ['Three.js', 'WebGL', 'React 19', 'Ephemeris Math', 'Node.js', 'DPDP Guardrails'],
      link: '/astrology',
      linkText: 'Explore Astrology Engine',
      isExternal: false,
      icon: <SparklesIcon />,
    },
    {
      id: 'invoicegen',
      category: 'saas',
      title: 'InvoiceGen',
      subtitle: '3D Smart GST Invoicing & Automated Tax Ledger',
      badge: 'B2B SAAS',
      metric: '100% Tax Compliant • Sub-second PDF Render',
      desc: 'Engineered for Indian businesses, freelancing studios, and agencies. Dynamically calculates CGST, SGST, IGST tax tiers and renders publication-ready vector PDFs directly in the browser.',
      highlights: [
        'Client-side vector PDF synthesis eliminating sensitive server data leakage',
        'Local-first offline state persistence with zero downtime resilience',
        'Real-time tax balance reconciliation and multi-currency formatting',
      ],
      techStack: ['React', 'jsPDF', 'HTML2Canvas', 'LocalStorage', 'Pure CSS3'],
      link: '/invoicegen',
      linkText: 'Launch InvoiceGen App',
      isExternal: false,
      icon: <DocIcon />,
    },
    {
      id: 'seo-engine',
      category: 'seo',
      title: 'Technical SEO & Search Dominance Engine',
      subtitle: 'Sub-Second Core Web Vitals & Semantic Knowledge Graphs',
      badge: 'CORE EXPERTISE',
      metric: 'LCP < 1.2s • CLS < 0.02 • AEO Optimized',
      desc: 'Our proprietary search optimization architecture designed for both Google search crawlers and Generative AI Answer Engines (Perplexity, ChatGPT Search, Gemini).',
      highlights: [
        'LCP < 1.2s, CLS < 0.02, and INP < 120ms benchmarked on mobile 4G networks',
        'Automated deep Schema.org semantic graphs (Organization, Software, Article)',
        'Edge dynamic server-side pre-rendering delivering instant crawlability',
      ],
      techStack: ['Edge SSR', 'Schema.org JSON-LD', 'Core Web Vitals', 'AEO', 'Cloudflare Workers'],
      link: '#metrics',
      linkText: 'Inspect Performance Benchmarks',
      isExternal: false,
      icon: <SearchIcon />,
    },
    {
      id: 'attendmax',
      category: 'cloud',
      title: 'AttendMax',
      subtitle: 'Biometric Workforce Telemetry & Personnel Choreography',
      badge: 'ENTERPRISE SYSTEM',
      metric: 'Real-Time Biometric Webhooks • Shift Rostering',
      desc: 'Enterprise workforce telemetry platform designed for distributed manufacturing units and tech parks. Automates biometric device synchronization, shift choreography, and anomaly tracking.',
      highlights: [
        'Webhook ingestion pipelines communicating with physical biometric hardware',
        'Role-based access matrix and enterprise shift pattern automation',
        'Interactive executive presentation deck deployed in production',
      ],
      techStack: ['Spring Boot', 'Webhooks', 'PostgreSQL', 'Biometric Telemetry', 'Docker'],
      link: '/AttendMax_Presentation.html',
      linkText: 'View AttendMax Deck',
      isExternal: true,
      icon: <UsersIcon />,
    },
    {
      id: 'newsapp',
      category: 'cloud',
      title: 'NewsApp',
      subtitle: 'High-Velocity Digital Newsroom Microservices Mesh',
      badge: 'HIGH CONCURRENCY',
      metric: 'Spring Boot Mesh • Apache Kafka Pub/Sub',
      desc: 'Distributed editorial publishing engine architected to withstand breaking news traffic spikes. Utilizes decoupled Spring Boot microservices, Kafka event streams, and edge CDN cache invalidation.',
      highlights: [
        'Event-driven publish/subscribe pipelines for instantaneous article distribution',
        'Multi-region read replicas with zero single point of failure',
        'Modular REST & GraphQL APIs powering web and mobile consumer clients',
      ],
      techStack: ['Spring Cloud', 'Apache Kafka', 'Redis Mesh', 'PostgreSQL', 'AWS ECS'],
      link: '/NewsApp-Presentation.html',
      linkText: 'Explore NewsApp Engine',
      isExternal: true,
      icon: <CloudIcon />,
    },
    {
      id: 'atulya-yuva',
      category: 'csr',
      title: 'Atulya Yuva Foundation',
      subtitle: 'Digital Philanthropy & Life-Saving Emergency Networks',
      badge: 'SOCIAL IMPACT',
      metric: '10,000+ Citizens Impacted • Emergency Bloodline',
      desc: 'Bramhaware’s dedicated CSR and non-profit digital division. We build and maintain the digital backbone that coordinates youth mentorship, social leadership programs, and life-saving blood donor networks across Maharashtra.',
      highlights: [
        'Automated emergency blood donor proximity matching system',
        'Free career and technology orientation for rural college students',
        '10,000+ youths engaged across community initiatives and eco-drives',
      ],
      techStack: ['React', 'Geolocation API', 'SMS/WhatsApp Conduits', 'Non-Profit Infrastructure'],
      link: 'http://atulyayuvafoundation.com/',
      linkText: 'Visit Foundation Portal',
      isExternal: true,
      icon: <ActivityIcon />,
    },
    {
      id: 'brilliant-share-market',
      category: 'saas',
      title: 'Brilliant Share Market Academy',
      subtitle: 'Interactive 3D Stock Market & Trading Education Platform',
      badge: 'FINTECH EDTECH',
      metric: '1,000+ Traders Trained • Real-Time 3D Candlestick Engine',
      desc: 'Engineered as a high-engagement 3D financial education portal for Pune’s premier stock market academy. Features real-time index tick feeds, position sizing calculators, interactive 3D WebGL charts, and direct multi-channel WhatsApp/Email batch enrollment.',
      highlights: [
        'Interactive 3D WebGL candlestick visualization with real-time bullish/bearish depth',
        'Built-in trade position sizing & capital risk allocation calculator',
        'Direct multi-dispatch enrollment engine (WhatsApp pre-filled text, Gmail & Outlook)',
      ],
      techStack: ['Three.js', 'React 19', 'WebGL', 'Risk Algorithms', 'WhatsApp API', 'Tailwind/CSS3'],
      link: '/brilliantsharemarket',
      linkText: 'Explore 3D Trading Portal',
      isExternal: false,
      icon: <LayersIcon />,
    },
    {
      id: 'nst-studio',
      category: 'client',
      title: 'NST Studio',
      subtitle: 'Cinematic Storytelling Studio Website — Nikunj Storytelling Studio',
      badge: 'CLIENT WEBSITE',
      metric: 'Branding • Film • Animation • Learning',
      desc: 'Designed and engineered the web home of Nikunj Storytelling Studio (NST), a storytelling-driven creative studio for branding, film, animation and creative education. Built from the studio’s own prototype and brand guide around one belief: Story First.',
      highlights: [
        'Real-time 3D WebGL spotlight stage with the NST storyteller floating in the beam',
        '"Story First." landing sequence, project archive and an in-page multi-page brand dossier',
        'Mobile-first layouts with reduced-motion and reduced-transparency fallbacks',
      ],
      techStack: ['React 19', 'TypeScript', 'Three.js', 'React Three Fiber', 'GSAP', 'Vite'],
      link: 'https://nststudioin.netlify.app/',
      linkText: 'Visit NST Studio',
      isExternal: true,
      icon: <SparklesIcon />,
    },
    {
      id: 'architecture-vault',
      category: 'cloud',
      title: 'Zero-Slop Architectural Standard',
      subtitle: '3-Tier Decoupled Elasticity & Cryptographic Isolation',
      badge: 'ENGINEERING DISCIPLINE',
      metric: '99.9% Cloud Uptime • AES-256 Multi-Tenant Vault',
      desc: 'Every system we ship adheres to our strict 3-Tier decoupled doctrine. Tier 1 handles Global Edge CDN and SSL termination, Tier 2 runs the Spring Boot logic mesh, and Tier 3 isolates persistent data into encrypted multi-tenant vaults.',
      highlights: [
        'Full compliance with India’s Digital Personal Data Protection Act (DPDP)',
        'Zero vendor lock-in with containerized portable topologies',
        'Strict code typing, lint standards, and automated regression suites',
      ],
      techStack: ['AWS CloudFront', 'Docker', 'Kubernetes', 'PostgreSQL RLS', 'AES-256'],
      link: '/',
      linkText: 'View 3D Architecture Canvas',
      isExternal: false,
      icon: <ShieldIcon />,
    },
  ];

  const categoryCounts = bragItems.reduce((counts, item) => {
    counts[item.category] = (counts[item.category] || 0) + 1;
    return counts;
  }, {});

  // Filter items
  const filteredItems =
    activeCategory === 'all'
      ? bragItems
      : bragItems.filter((item) => item.category === activeCategory);

  return (
    <div className={`brag-page-root theme-${theme}`}>
      {/* SEO Metadata */}
      <SEO
        title="Brag Sheet & Production Track Record | BRAMHAWARE Technologies"
        description="Explore Bramhaware Technologies' official brag sheet: 2,400+ startups indexed across 28 states via The Foundora, 10,000+ lives impacted via Atulya Yuva Foundation, sub-second technical SEO, and battle-tested SaaS platforms."
        keywords="Bramhaware Brag Sheet, Bramhaware Portfolio, The Foundora, Dnyanaai Astrology, InvoiceGen, Technical SEO Pune, Software Development Achievements, Case Studies Pune"
        canonicalUrl="/brag"
        structuredData={structuredData}
      />

      {/* Top Luminous Progress Bar */}
      <div className="brag-top-bar" aria-hidden="true" />

      {/* Fixed Architectural Header */}
      <header className="brag-header">
        <div className="brag-header-inner">
          <Link to="/" className="brag-brand-lockup" title="Return to Bramhaware Masthead">
            <div className="brag-logo-crest">
              <img
                src="/favicon-brahmaware-removebg-preview.png"
                alt="Bramhaware Technologies Crest"
              />
            </div>
            <div className="brag-titles">
              <span className="brag-main-title">BRAMHAWARE</span>
              <span className="brag-sub-title">STUDIO BRAG SHEET</span>
            </div>
          </Link>

          <nav className="brag-nav" aria-label="Brag Sheet Navigation">
            <Link to="/" className="brag-nav-link">
              <ArrowLeftIcon className="ui-icon" />
              <span>Studio Masthead</span>
            </Link>
            <a href="#numbers" className="brag-nav-link">
              The Numbers
            </a>
            <a href="#milestones" className="brag-nav-link">
              Shipped Platforms
            </a>
            <a href="#standards" className="brag-nav-link">
              Studio Tenets
            </a>
            <a href="#press" className="brag-nav-link">
              Press &amp; RFP
            </a>
          </nav>

          {/* Telemetry Badge */}
          <div className="brag-telemetry-badge">
            <span className="telemetry-pulse-dot" />
            <span className="telemetry-label">PRODUCTION VERIFIED</span>
          </div>

          <div className="brag-header-actions">
            {/* Theme Toggle */}
            <button
              type="button"
              className="btn-brag-icon"
              onClick={handleToggleTheme}
              title={theme === 'bright' ? 'Switch to Obsidian Night Mode' : 'Switch to Luminous Bright Mode'}
              aria-label="Toggle Theme"
            >
              {theme === 'bright' ? <MoonIcon className="ui-header-icon" /> : <SunIcon className="ui-header-icon" />}
            </button>

            {/* Quick RFP / Pitch Copy */}
            <button
              type="button"
              className="btn-brag-outline"
              onClick={handleCopyRfp}
              title="Copy Studio Summary for RFPs"
            >
              {copiedRfp ? <CheckIcon className="ui-icon green" /> : <CopyIcon className="ui-icon" />}
              <span>{copiedRfp ? 'Copied!' : 'Copy RFP Summary'}</span>
            </button>

            {/* Primary CTA */}
            <a href="mailto:info@bramhaware.in?subject=BRAMHAWARE%20Project%20Inquiry" className="btn-brag-primary">
              <span>Hire Studio →</span>
            </a>
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="brag-container">
        {/* ===================================================================
            HERO MASTHEAD: UNAPOLOGETIC TRACK RECORD
            =================================================================== */}
        <section className="brag-hero-section">
          <div className="brag-hero-content">
            <div className="brag-pill-eyebrow">
              <TrophyIcon className="ui-icon gold" />
              <span>BRAMHAWARE SOFTWARE STUDIO // PRODUCTION RECORD &amp; ACCOMPLISHMENTS</span>
            </div>

            <h1 className="brag-hero-title">
              We Don&apos;t Just Pitch Software.
              <br />
              <span className="brag-accent-gradient">We Ship National Platforms &amp; Core Engines.</span>
            </h1>

            <p className="brag-hero-desc">
              A transparent, unvarnished log of what the Bramhaware engineering studio has conceptualized, architected, shipped, and scaled. From national venture indexes spanning 28 states to sub-second technical SEO and digital philanthropy.
            </p>

            <div className="brag-hero-actions">
              <button
                type="button"
                className="btn-hero-primary"
                onClick={handleDownloadBragPDF}
                disabled={isPdfGenerating}
              >
                <DocIcon className="ui-icon" />
                <span>{isPdfGenerating ? 'Generating PDF...' : 'Download Verified PDF Dossier'}</span>
              </button>

              <button
                type="button"
                className="btn-hero-outline"
                onClick={handleCopyShareLink}
              >
                {copiedLink ? <CheckIcon className="ui-icon green" /> : <CopyIcon className="ui-icon" />}
                <span>{copiedLink ? 'Link Copied to Clipboard!' : 'Share Brag Sheet'}</span>
              </button>

              <a
                href="mailto:info@bramhaware.in?subject=Bramhaware%20Engineering%20Collaboration"
                className="btn-hero-secondary"
              >
                <span>Partner with Us →</span>
              </a>
            </div>
          </div>
        </section>

        {/* ===================================================================
            SECTION: THE NUMBERS & SCALE (STAT GRID)
            =================================================================== */}
        <section id="numbers" className="brag-numbers-section">
          <div className="section-head-box">
            <span className="section-eyebrow">01 // VERIFIED PRODUCTION METRICS</span>
            <h2 className="section-title">The Numbers We Stand By</h2>
            <p className="section-subtitle">
              Every metric below represents active code, live databases, and real people impacted.
            </p>
          </div>

          <div className="brag-stats-grid">
            <div className="brag-stat-card">
              <div className="stat-card-icon sapphire"><LayersIcon /></div>
              <div className="stat-big-num">2,400+</div>
              <div className="stat-label">Startups &amp; Ventures Indexed</div>
              <p className="stat-subtext">Across 28 Indian States &amp; Union Territories on The Foundora network.</p>
            </div>

            <div className="brag-stat-card">
              <div className="stat-card-icon emerald"><UsersIcon /></div>
              <div className="stat-big-num">10,000+</div>
              <div className="stat-label">Citizens &amp; Youth Empowered</div>
              <p className="stat-subtext">Via Atulya Yuva Foundation social infrastructure and emergency bloodlines.</p>
            </div>

            <div className="brag-stat-card">
              <div className="stat-card-icon gold"><BoltIcon /></div>
              <div className="stat-big-num">99.9%</div>
              <div className="stat-label">Cloud Uptime Architecture</div>
              <p className="stat-subtext">Engineered with 3-tier decoupled resilience and zero single points of failure.</p>
            </div>

            <div className="brag-stat-card">
              <div className="stat-card-icon cyan"><SearchIcon /></div>
              <div className="stat-big-num">&lt; 1.2s</div>
              <div className="stat-label">Average LCP Core Web Vitals</div>
              <p className="stat-subtext">Proprietary edge pre-rendering and Schema.org semantic graphs for search dominance.</p>
            </div>

            <div className="brag-stat-card">
              <div className="stat-card-icon sapphire"><CodeIcon /></div>
              <div className="stat-big-num">100+</div>
              <div className="stat-label">Production Deployments</div>
              <p className="stat-subtext">Continuous delivery cycles across web applications, mobile apps, and microservices.</p>
            </div>

            <div className="brag-stat-card">
              <div className="stat-card-icon emerald"><ShieldIcon /></div>
              <div className="stat-big-num">100%</div>
              <div className="stat-label">In-House Engineering</div>
              <p className="stat-subtext">Zero outsourced spaghetti code. Every line crafted with precision in Pune, India.</p>
            </div>
          </div>
        </section>

        {/* ===================================================================
            SECTION: SHIPPED PLATFORMS & MILESTONES (FILTERABLE SHOWCASE)
            =================================================================== */}
        <section id="milestones" className="brag-showcase-section">
          <div className="section-head-box">
            <span className="section-eyebrow">02 // THE PORTFOLIO OF WINS</span>
            <h2 className="section-title">Shipped Software &amp; Architectural Feats</h2>
            <p className="section-subtitle">
              Filter through our flagship ventures, proprietary algorithms, enterprise platforms, and community impact.
            </p>
          </div>

          {/* Interactive Filter Pills */}
          <div className="brag-filter-pills-row">
            <button
              type="button"
              className={`filter-pill ${activeCategory === 'all' ? 'active' : ''}`}
              onClick={() => setActiveCategory('all')}
            >
              All Accomplishments ({bragItems.length})
            </button>
            <button
              type="button"
              className={`filter-pill ${activeCategory === 'saas' ? 'active' : ''}`}
              onClick={() => setActiveCategory('saas')}
            >
              Flagship SaaS &amp; Platforms ({categoryCounts.saas || 0})
            </button>
            <button
              type="button"
              className={`filter-pill ${activeCategory === 'seo' ? 'active' : ''}`}
              onClick={() => setActiveCategory('seo')}
            >
              Technical SEO &amp; AEO ({categoryCounts.seo || 0})
            </button>
            <button
              type="button"
              className={`filter-pill ${activeCategory === 'cloud' ? 'active' : ''}`}
              onClick={() => setActiveCategory('cloud')}
            >
              Cloud &amp; Microservices ({categoryCounts.cloud || 0})
            </button>
            <button
              type="button"
              className={`filter-pill ${activeCategory === 'csr' ? 'active' : ''}`}
              onClick={() => setActiveCategory('csr')}
            >
              Social Conscience &amp; CSR ({categoryCounts.csr || 0})
            </button>
            <button
              type="button"
              className={`filter-pill ${activeCategory === 'client' ? 'active' : ''}`}
              onClick={() => setActiveCategory('client')}
            >
              Client Websites ({categoryCounts.client || 0})
            </button>
          </div>

          {/* Cards Grid */}
          <div className="brag-cards-grid">
            {filteredItems.map((item) => (
              <article key={item.id} className="brag-detail-card">
                <div className="card-top-bar">
                  <div className="card-badge-pill">{item.badge}</div>
                  <div className="card-metric-highlight">{item.metric}</div>
                </div>

                <div className="card-header-block">
                  <div className="card-icon-crest">{item.icon}</div>
                  <div className="card-titles">
                    <h3 className="card-title">{item.title}</h3>
                    <p className="card-subtitle">{item.subtitle}</p>
                  </div>
                </div>

                <p className="card-description">{item.desc}</p>

                <div className="card-highlights-box">
                  <span className="highlights-title">Engineering Proof Points:</span>
                  <ul className="highlights-list">
                    {item.highlights.map((point, idx) => (
                      <li key={idx}>
                        <span className="check-bullet">✓</span>
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="card-tech-tags">
                  {item.techStack.map((tech, idx) => (
                    <span key={idx} className="tech-tag">
                      {tech}
                    </span>
                  ))}
                </div>

                <div className="card-actions-footer">
                  {item.isExternal ? (
                    <a
                      href={item.link}
                      target="_blank"
                      rel="noreferrer"
                      className="btn-card-link"
                    >
                      <span>{item.linkText}</span>
                      <ExternalLinkIcon className="ui-icon" />
                    </a>
                  ) : (
                    <Link to={item.link} className="btn-card-link">
                      <span>{item.linkText}</span>
                      <ArrowRightIcon className="ui-icon" />
                    </Link>
                  )}
                </div>
              </article>
            ))}
          </div>
        </section>

        {/* ===================================================================
            SECTION: STUDIO TENETS & WHY WE BRAG
            =================================================================== */}
        <section id="standards" className="brag-tenets-section">
          <div className="section-head-box">
            <span className="section-eyebrow">03 // THE CODE OF CRAFTSMANSHIP</span>
            <h2 className="section-title">The Four Bramhaware Tenets</h2>
            <p className="section-subtitle">
              Why our clients trust us with their most critical digital architectures.
            </p>
          </div>

          <div className="tenets-grid">
            <div className="tenet-card">
              <div className="tenet-num">01</div>
              <h4>Zero-Slop Code Quality</h4>
              <p>
                We do not produce disposable MVPs that collapse under traffic. Every function, schema, and API endpoint is typed, tested, and built for 5+ years of maintenance.
              </p>
            </div>

            <div className="tenet-card">
              <div className="tenet-num">02</div>
              <h4>Sub-Second Speed by Default</h4>
              <p>
                Speed is an engineering feature, not an afterthought. We treat any webpage load time over 1.5 seconds as a critical architectural defect.
              </p>
            </div>

            <div className="tenet-card">
              <div className="tenet-num">03</div>
              <h4>Multi-Tenant Cryptographic Vaults</h4>
              <p>
                Zero tolerance for security leaks. We isolate client data with Row-Level Security, AES-256 persistence encryption, and full DPDP Act compliance.
              </p>
            </div>

            <div className="tenet-card">
              <div className="tenet-num">04</div>
              <h4>Founder-Led Architecture</h4>
              <p>
                When you collaborate with Bramhaware, you talk directly with senior software architects and systems designers who write production code every single day.
              </p>
            </div>
          </div>
        </section>

        {/* ===================================================================
            SECTION: MILESTONE TIMELINE (2024 - 2026)
            =================================================================== */}
        <section className="brag-timeline-section">
          <div className="section-head-box">
            <span className="section-eyebrow">04 // CHRONOLOGICAL MOMENTUM</span>
            <h2 className="section-title">Our Evolution in Production</h2>
            <p className="section-subtitle">
              A rapid journey from studio inception to multi-product national scale.
            </p>
          </div>

          <div className="timeline-spine-wrapper">
            <div className="timeline-node-item">
              <div className="timeline-date-tag">2026 // Q1</div>
              <div className="timeline-card">
                <span className="timeline-cat-pill">NATIONAL REACH</span>
                <h4>The Foundora Expands Across 28 States</h4>
                <p>
                  The Foundora indices over 2,400 ventures across India, becoming a staple for emerging founder discovery. Dnyanaai Astrology 2.0 computational engine deployed with real-time celestial telemetry.
                </p>
              </div>
            </div>

            <div className="timeline-node-item">
              <div className="timeline-date-tag">2025 // Q3-Q4</div>
              <div className="timeline-card">
                <span className="timeline-cat-pill">SAAS &amp; SEO LAUNCHES</span>
                <h4>InvoiceGen &amp; Technical SEO Framework Released</h4>
                <p>
                  Rolled out the 3D Smart GST Invoicing suite and open-sourced our high-rank technical SEO pre-rendering architecture for enterprise clients.
                </p>
              </div>
            </div>

            <div className="timeline-node-item">
              <div className="timeline-date-tag">2025 // Q1-Q2</div>
              <div className="timeline-card">
                <span className="timeline-cat-pill">ENTERPRISE SYSTEMS</span>
                <h4>AttendMax &amp; NewsApp Microservices Battle-Tested</h4>
                <p>
                  Deployed AttendMax biometric personnel sync for distributed industrial facilities; established Spring Boot microservices mesh for high-concurrency digital publishing.
                </p>
              </div>
            </div>

            <div className="timeline-node-item">
              <div className="timeline-date-tag">2024</div>
              <div className="timeline-card">
                <span className="timeline-cat-pill">FOUNDING &amp; CSR</span>
                <h4>Studio Inception &amp; Atulya Yuva Digital Foundation</h4>
                <p>
                  Bramhaware Technologies established in Pune, Maharashtra. Built the non-profit digital backbone for Atulya Yuva Foundation, reaching 10,000+ youths and establishing an emergency bloodline matching network.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ===================================================================
            SECTION: PRESS, RFP & SHAREABLE STUDIO PITCH
            =================================================================== */}
        <section id="press" className="brag-press-section">
          <div className="press-glass-box">
            <div className="press-narrative">
              <span className="section-eyebrow">05 // PRESS &amp; RFP QUICK-KIT</span>
              <h2 className="section-title">Need Bramhaware for Your RFP or Slide Deck?</h2>
              <p className="section-subtitle">
                Click below to copy our verified studio summary, verified stats, and technical capabilities directly to your clipboard.
              </p>
            </div>

            <div className="press-copy-terminal">
              <div className="terminal-header">
                <div className="terminal-dots">
                  <span className="dot red" />
                  <span className="dot yellow" />
                  <span className="dot green" />
                </div>
                <span className="terminal-file-name">bramhaware_studio_rfp.txt</span>
                <button
                  type="button"
                  className="btn-terminal-copy"
                  onClick={handleCopyRfp}
                >
                  {copiedRfp ? <CheckIcon className="ui-icon green" /> : <CopyIcon className="ui-icon" />}
                  <span>{copiedRfp ? 'Copied!' : 'Copy Text'}</span>
                </button>
              </div>

              <pre className="terminal-body">
                <code>{`BRAMHAWARE TECHNOLOGIES // PUNE, INDIA
Tagline: "Technology by Design, Creation by Bramha"
Services: Custom Web Apps, Technical SEO, Mobile Apps, Cloud & DevOps, SaaS Platforms.

TRACK RECORD:
- The Foundora: 2,400+ Startups Indexed (28 States)
- Dnyanaai Astrology: Proprietary Vedic Computational Algorithms
- InvoiceGen: 3D Smart GST Invoicing & Tax Ledger Engine
- Technical SEO: Sub-second (<1.2s LCP) Core Web Vitals & Semantic Graphs
- Social Impact: 10,000+ citizens empowered via Atulya Yuva Foundation

CONTACT: info@bramhaware.in | https://bramhaware.in`}</code>
              </pre>
            </div>
          </div>
        </section>

        {/* ===================================================================
            SECTION: THE LAUNCH VIDEO GENERATOR (latent-spaces/brag)
            =================================================================== */}
        <section id="video-generator" className="brag-video-section">
          <div className="section-head-box">
            <span className="section-eyebrow">06 // LAUNCH VIDEO GENERATOR (latent-spaces/brag)</span>
            <h2 className="section-title">Turn Your Codebase into a 20s Launch Video</h2>
            <p className="section-subtitle">
              Run <code>/brag</code> in your project folder. The AI scans your disk code, crafts a storyboard, generates background music and AI voiceover, and outputs a 1080p promo clip and ready-to-share social copy.
            </p>
          </div>

          <div className="video-generator-card">
            <div className="video-card-top">
              <div className="video-badge-pill">
                <SparklesIcon className="ui-icon gold" />
                <span>POWERED BY HYPERFRAMES &amp; LATENT-SPACES/BRAG</span>
              </div>
              <div className="cli-snippet-pill">
                <code>/brag --tone &quot;fake Series A launch from 2016&quot; --voice</code>
              </div>
            </div>

            <div className="video-player-mockup">
              <div className="video-screen-frame">
                <div className="video-screen-overlay">
                  <div className="video-scene-badge">SCENE 02/04 • 0:08 / 0:20</div>
                  <h3 className="video-screen-title">2,400+ Ventures Indexed across 28 States.</h3>
                  <p className="video-screen-sub">The Foundora — India&apos;s National Startup &amp; Investor Network</p>
                  <div className="video-audio-wave">
                    <span className="wave-bar" />
                    <span className="wave-bar" />
                    <span className="wave-bar" />
                    <span className="wave-bar" />
                    <span className="wave-bar" />
                    <span className="wave-label">AI NARRATION &amp; SYNTH BEAT ACTIVE</span>
                  </div>
                </div>
              </div>

              <div className="video-output-tray">
                <div className="tray-title">Generated Output Assets (<code>brag-output/</code>):</div>
                <div className="tray-grid">
                  <div className="tray-item">
                    <span className="tray-icon">▶</span>
                    <div>
                      <strong>brag.mp4</strong>
                      <p>1080p 60fps 20s launch clip</p>
                    </div>
                  </div>
                  <div className="tray-item">
                    <span className="tray-icon">📋</span>
                    <div>
                      <strong>storyboard.json</strong>
                      <p>Timed scenes &amp; motion cues</p>
                    </div>
                  </div>
                  <div className="tray-item">
                    <span className="tray-icon">🎙️</span>
                    <div>
                      <strong>script.txt</strong>
                      <p>AI narration &amp; voiceover brief</p>
                    </div>
                  </div>
                  <div className="tray-item">
                    <span className="tray-icon">🚀</span>
                    <div>
                      <strong>social_copy.md</strong>
                      <p>Ready-to-post X &amp; LinkedIn text</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ===================================================================
            SECTION: BOTTOM MASTER CALL TO ACTION
            =================================================================== */}
        <section className="brag-cta-section">
          <div className="brag-cta-card">
            <span className="cta-pill">READY TO SHIP GREAT SOFTWARE?</span>
            <h2 className="cta-title">Let&apos;s Build Something Worth Bragging About.</h2>
            <p className="cta-desc">
              Whether you need a custom web platform, a high-concurrency microservice backplane, or high-rank technical SEO that drives real business results, our Pune engineering studio is ready.
            </p>

            <div className="cta-buttons-cluster">
              <a
                href="mailto:info@bramhaware.in?subject=BRAMHAWARE%20Project%20Scope%20Inquiry"
                className="btn-cta-main"
              >
                <MailIcon className="ui-icon" />
                <span>Start a Project with Us →</span>
              </a>

              <Link to="/" className="btn-cta-alt">
                <span>Explore Interactive 3D Studio →</span>
              </Link>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="brag-footer">
        <div className="brag-footer-inner">
          <div className="footer-brand">
            <img src="/favicon-brahmaware-removebg-preview.png" alt="Bramhaware Crest" />
            <span>BRAMHAWARE TECHNOLOGIES &bull; PUNE, INDIA</span>
          </div>
          <div className="footer-links">
            <Link to="/">Studio Masthead</Link>
            <Link to="/invoicegen">InvoiceGen</Link>
            <Link to="/astrology">Dnyanaai Astrology</Link>
            <a href="https://thefoundora.com" target="_blank" rel="noreferrer">The Foundora ↗</a>
            <a href="http://atulyayuvafoundation.com/" target="_blank" rel="noreferrer">Atulya Yuva ↗</a>
          </div>
          <div className="footer-copy">
            &copy; {new Date().getFullYear()} BRAMHAWARE Technologies. All rights reserved. Zero-slop engineering.
          </div>
        </div>
      </footer>
    </div>
  );
}
